import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress
} from '@mui/material';
import { PaidOutlined, CloseOutlined } from '@mui/icons-material';

import { creditEmployeeBalance } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { formatINR } from './creditLimitsUtils';

const CreditBalanceModal = ({ open, onClose, account, onSuccess }) => {
    const [creditBalanceForm, setCreditBalanceForm] = useState({
        cross_user_id: '',
        amount: '',
        category: 'TA',
        custom_category: '',
        remarks: ''
    });
    const [isCreditingBalance, setIsCreditingBalance] = useState(false);

    useEffect(() => {
        if (account && open) {
            setCreditBalanceForm({
                cross_user_id: account?.cross_user_id ?? account?.crossbanking_user_id ?? account?.id ?? '',
                amount: '',
                category: 'TA',
                custom_category: '',
                remarks: ''
            });
        }
    }, [account, open]);

    const handleCreditBalanceSubmit = async (e) => {
        e.preventDefault();
        const crossUserId = parseInt(creditBalanceForm.cross_user_id || account?.cross_user_id || account?.id, 10);
        if (!crossUserId || isNaN(crossUserId)) {
            globalNotifyError('Unable to identify employee user ID');
            return;
        }

        const amount = parseFloat(creditBalanceForm.amount);
        if (isNaN(amount) || amount <= 0) {
            globalNotifyError('Please enter a valid amount greater than 0');
            return;
        }

        setIsCreditingBalance(true);
        try {
            const chosenCategory =
                creditBalanceForm.category === 'Other' ? creditBalanceForm.custom_category?.trim() || 'Other' : creditBalanceForm.category;

            const payload = {
                cross_user_id: crossUserId,
                amount: Number(amount),
                ...(chosenCategory && { category: chosenCategory }),
                ...(creditBalanceForm.remarks?.trim() && { remarks: creditBalanceForm.remarks.trim() })
            };

            const response = await creditEmployeeBalance(payload);

            if (response?.data?.success) {
                onClose();
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error crediting employee balance:', error);
            handleCatchError(error);
        } finally {
            setIsCreditingBalance(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !isCreditingBalance && onClose()}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#f0fdf4', color: '#16a34a', display: 'flex' }}>
                        <PaidOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Credit Wallet Balance
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Add funds to employee Crossbanking wallet
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} disabled={isCreditingBalance}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleCreditBalanceSubmit}>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2.5}>
                        {/* Employee Info Card */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block">
                                Employee Account
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {account?.employee_name || account?.name || account?.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {account?.email}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Balance
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#16a34a' }}>
                                        {formatINR(account?.crossbanking_balance ?? 0)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Amount */}
                        <TextField
                            label="Amount to Credit (₹)"
                            required
                            fullWidth
                            type="number"
                            value={creditBalanceForm.amount}
                            onChange={(e) => setCreditBalanceForm({ ...creditBalanceForm, amount: e.target.value })}
                            placeholder="e.g. 5000"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                            }}
                            helperText="Amount must be greater than 0"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />

                        {/* Category */}
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={creditBalanceForm.category}
                                label="Category"
                                onChange={(e) => setCreditBalanceForm({ ...creditBalanceForm, category: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="TA">TA (Travel Allowance)</MenuItem>
                                <MenuItem value="Bonus">Bonus</MenuItem>
                                <MenuItem value="Allowance">Allowance</MenuItem>
                                <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                                <MenuItem value="Salary Advance">Salary Advance</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Custom Category if 'Other' */}
                        {creditBalanceForm.category === 'Other' && (
                            <TextField
                                label="Specify Category"
                                fullWidth
                                value={creditBalanceForm.custom_category}
                                onChange={(e) => setCreditBalanceForm({ ...creditBalanceForm, custom_category: e.target.value })}
                                placeholder="e.g. Project Incentive"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        )}

                        {/* Remarks */}
                        <TextField
                            label="Remarks"
                            fullWidth
                            multiline
                            rows={2}
                            value={creditBalanceForm.remarks}
                            onChange={(e) => setCreditBalanceForm({ ...creditBalanceForm, remarks: e.target.value })}
                            placeholder="e.g. Travel Allowance for September 2026"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={isCreditingBalance} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isCreditingBalance || !creditBalanceForm.amount}
                        startIcon={isCreditingBalance ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#16a34a',
                            '&:hover': { bgcolor: '#15803d' }
                        }}
                    >
                        {isCreditingBalance ? 'Crediting...' : 'Credit Balance'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

CreditBalanceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    account: PropTypes.object,
    onSuccess: PropTypes.func
};

export default CreditBalanceModal;
